import type { ReactNode } from 'react';
import { DrawerSection } from '@components/DrawerSection';
import { Show, ShowFirstMatching } from '@components/Show';
import type { BlockEditFormState } from '@ContractBuilder/modules/block-edit';

import { shouldRenderField } from '../utils/should-render-field';

import { AdminViewSkeleton } from './AdminViewSkeleton';
import { ContractPillSkeleton } from './ContractPillSkeleton';
import { ContractValuePills } from './ContractValuePills';

export type DatapointDisplayKey = keyof Pick<
  BlockEditFormState,
  'teams' | 'class_of_business' | 'inception_date' | 'expiry_date'
>;

/**
 * The scalar rendered as a single pill — the element type for array fields, the value itself
 * otherwise. The `& (string | number)` looks redundant (every current key resolves to one) but is
 * required: ContractValuePills' `Value extends string | number` constraint cannot be proven for the
 * open generic K, only per concrete key.
 */
type DatapointPillValue<K extends DatapointDisplayKey> = (NonNullable<BlockEditFormState[K]> extends ReadonlyArray<
  infer Item
>
  ? Item
  : NonNullable<BlockEditFormState[K]>) &
  (string | number);

interface DatapointInputProps<K extends DatapointDisplayKey> {
  value: BlockEditFormState[K];
  onChange: (value: BlockEditFormState[K] | null | undefined) => void;
}

interface DatapointDisplayProps<K extends DatapointDisplayKey> extends DatapointInputProps<K> {
  getContractValuePillsLabel?: (value: DatapointPillValue<K>) => string;
  isAdminView: boolean;
  isLoading?: boolean;
  renderInput: (props: DatapointInputProps<K>) => ReactNode;
  title: string;
}

export const DatapointDisplay = <K extends DatapointDisplayKey>({
  getContractValuePillsLabel,
  isAdminView,
  isLoading = false,
  onChange,
  renderInput,
  title,
  value,
}: DatapointDisplayProps<K>) => {
  if (!shouldRenderField(isAdminView, value)) {
    return null;
  }

  const pillValues = (Array.isArray(value) ? value : [value]) as Array<DatapointPillValue<K> | null | undefined>;

  return (
    <DrawerSection title={title}>
      <ShowFirstMatching fallback={<ContractValuePills data={pillValues} getLabel={getContractValuePillsLabel} />}>
        <Show when={isAdminView && isLoading}>
          <AdminViewSkeleton />
        </Show>
        <Show when={isAdminView}>{renderInput({ value, onChange })}</Show>
        <Show when={isLoading}>
          <ContractPillSkeleton />
        </Show>
      </ShowFirstMatching>
    </DrawerSection>
  );
};
